import { useEffect, useMemo, useState } from 'react';

interface InstallOutcome {
  outcome: 'accepted' | 'dismissed' | 'unavailable';
}

export default function usePWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const isIOS = useMemo(() => /iphone|ipad|ipod/i.test(navigator.userAgent), []);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      // Most browsers expect preventDefault to allow custom prompt
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const onAppInstalled = () => {
      setCanInstall(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as any);
    window.addEventListener('appinstalled', onAppInstalled);

    const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
    setIsStandalone(!!standalone);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as any);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<InstallOutcome> => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
      return { outcome: choiceResult.outcome };
    } catch {
      return { outcome: 'unavailable' };
    }
  };

  return {
    canInstall: canInstall && !isStandalone,
    isStandalone,
    isIOS,
    promptInstall,
  } as const;
}
